import { v4 } from 'uuid';
import join from 'url-join';
import type { ResponseCookie } from '@edge-runtime/cookies';
import { createInstance, OptimizelyDecideOption } from '@optimizely/optimizely-sdk/dist/optimizely.lite.min.js';
import type { EdgeRequest, EdgeResponse } from '../next-use';

export const overrideCookie = 'x-petm-override';
export const decisionsCookie = 'x-petm-decisions';
export const visitorIdCookie = 'x-petm-visitor-id';
export type Decisions = Record<string, unknown>;

export class Optimizely {
    private readonly userId: string;

    constructor(
        private readonly request: EdgeRequest,
        private readonly response: EdgeResponse
    ) {
        this.userId = this.getUserId();
    }

    private async getDatafile() {
        const edgeConfig = this.request.edgeConfig;

        if (!edgeConfig) {
            return await fetch(
                join('https://cdn.optimizely.com/datafiles', `${process.env.NEXT_PUBLIC_OPTIMIZELY_SDK_KEY}.json`)
            ).then(r => r.json());
        }

        return edgeConfig.optimizely;
    }

    private async decideAll() {
        const instance = createInstance({
            datafile: await this.getDatafile(),
            clientEngine: 'javascript-sdk/vercel-edge',
        });

        const decisions = instance
            ?.createUserContext(this.userId, {
                Domain: this.request.originalUrl.host,
                App: this.request.app ?? process.env.NEXT_PUBLIC_APP_NAME,
                Pathname: this.request.originalUrl.pathname,
            })
            ?.decideAll([OptimizelyDecideOption.DISABLE_DECISION_EVENT]);

        return Object.fromEntries(
            Object.entries(decisions ?? {}).map(([key, { variables }]) => [key, variables.value])
        );
    }

    private getUserId() {
        const userId = this.request.cookies.get(visitorIdCookie)?.value || v4();
        this.setCookie(visitorIdCookie, userId, {
            expires: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        });

        return userId;
    }

    private getSearch() {
        return this.request.parsedUrl.searchParams.getAll('override').reduce<Decisions>((acc, item) => {
            const [key, value] = item.split(':');
            acc[key] = value === 'true' ? true : value === 'false' ? false : value;
            return acc;
        }, {});
    }

    private getCookie() {
        return JSON.parse(this.request.cookies.get(overrideCookie)?.value ?? '{}') as Decisions;
    }

    private setCookie(key: string, value: string, options: Partial<ResponseCookie> = {}) {
        const isUS = this.request.originalUrl.host.includes('petsmart.com');
        const isCA = this.request.originalUrl.host.includes('petsmart.ca');

        this.request.cookies.set(key, value);
        this.response.cookies.set(key, value, {
            ...options,
            ...(isUS && { domain: '.petsmart.com' }),
            ...(isCA && { domain: '.petsmart.ca' }),
        });
    }

    public async getDecisions() {
        const all = await this.decideAll();
        const cookie = this.getCookie();
        const search = this.getSearch();

        const override = Object.fromEntries(Object.entries({ ...cookie, ...search }).filter(([key]) => key in all));

        const decisions = Object.entries({ ...all, ...override })
            .filter(([key]) => key in all)
            .sort(([a], [b]) => a.localeCompare(b))
            .reduce<Decisions>((acc, [key, value]) => {
                if (value !== false) acc[key] = value;
                return acc;
            }, {});

        if (Object.keys(override).length) {
            this.setCookie(overrideCookie, JSON.stringify(override));
        }

        if (Object.keys(decisions).length) {
            this.setCookie(decisionsCookie, JSON.stringify(decisions));
        }

        return decisions;
    }
}
